import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const BACKEND_URL = RAW_BACKEND_URL ? RAW_BACKEND_URL.replace(/\/$/, '') : (
  typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8000'
    : 'https://api.coupleinapp.com'
);

const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const AdminContext = createContext(null);

export function AdminProvider({ children }) {
  const [token, setToken] = useState(() => sessionStorage.getItem('blog_admin_token') || '');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    if (token) {
      sessionStorage.setItem('blog_admin_token', token);
    } else {
      sessionStorage.removeItem('blog_admin_token');
    }
  }, [token]);

  const login = async (passcode) => {
    if (!passcode?.trim()) {
      setAuthError('Please enter admin passcode');
      return false;
    }
    setAuthLoading(true);
    setAuthError('');
    try {
      const res = await axios.post(`${API}/blog/admin/verify`, { passcode }, { withCredentials: false });
      const receivedToken = res.data.token;
      setToken(receivedToken);
      sessionStorage.setItem('blog_admin_token', receivedToken);
      toast.success('Authenticated as Admin');
      return true;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Invalid passcode';
      setAuthError(msg);
      toast.error(msg);
      return false;
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = () => {
    setToken('');
    sessionStorage.removeItem('blog_admin_token');
    toast.info('Logged out of Admin');
  };

  return (
    <AdminContext.Provider
      value={{
        token,
        authed: Boolean(token),
        login,
        logout,
        authLoading,
        authError,
        setAuthError,
        API,
        BACKEND_URL,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}

export default AdminContext;
